/**
 * DEVLOGS-TEMP — assembles a config into a live logger.
 *
 * Shared by every adapter so the wiring order (and the loop guards) exist in
 * exactly one place.
 */

import { createBatcher } from "./batcher.ts";
import type { DevLogConfig } from "./config.ts";
import { compactContext, getDevLogContext } from "./context.ts";
import { createDevLogger, type DevLogger } from "./logger.ts";
import { NamespaceRateLimiter } from "./rate-limit.ts";
import { createMonotonicClock, newRunId, newSpanId, SeqCounter } from "./session.ts";
import { createHttpTransport, type DevLogTransport } from "./transport.ts";
import { configureDevLog } from "./runtime.ts";
import type { DevLogSurface } from "./types.ts";

export interface DevLogHandle {
  logger: DevLogger;
  runId: string;
  /** Origins/URLs the fetch capture must never log — guard #2 against the loop. */
  ignoreUrls: readonly string[];
  /** True while the transport is sending — guard #3. */
  isSending(): boolean;
  flush(): void;
  uninstall(): void;
}

export interface InstallOptions {
  config: DevLogConfig;
  surface: DevLogSurface;
  /** MUST be captured before any fetch wrapper is installed. */
  fetchImpl: typeof fetch;
  platform?: string;
  label?: string;
  beacon?: ((url: string, body: string) => boolean) | null;
  /** Overridable for tests. */
  now?: () => number;
  transport?: DevLogTransport;
}

export function installDevLog(options: InstallOptions): DevLogHandle | null {
  const config = options.config;
  const url = config.url;
  if (!config.enabled || url === null) return null;

  const now = options.now ?? Date.now;
  const clock = createMonotonicClock(now);
  const seq = new SeqCounter();
  const runId = newRunId(clock.now());
  const limiter = new NamespaceRateLimiter(undefined, () => clock.now());

  let sending = false;

  const transport =
    options.transport ??
    createHttpTransport({
      url,
      token: config.token,
      fetchImpl: options.fetchImpl,
      timeoutMs: config.timeoutMs,
      beacon: options.beacon ?? null,
      onInFlight: (inFlight) => {
        sending = inFlight;
      },
    });

  const batcher = createBatcher({
    transport,
    envelope: () => {
      const context = compactContext(getDevLogContext());
      return {
        runId,
        surface: options.surface,
        env: config.envName,
        ...(options.platform ? { platform: options.platform } : {}),
        ...(options.label ? { label: options.label } : {}),
        ...context,
      };
    },
    drops: () => limiter.takeDrops(),
    maxEntries: config.maxEntries,
    maxBytes: config.maxBytes,
    batchSize: config.batchSize,
    batchBytes: config.batchBytes,
    flushMs: config.flushMs,
    now,
  });

  const logger = createDevLogger({
    batcher,
    clock,
    seq,
    filter: config.filter,
    minLevel: config.minLevel,
    limiter,
    saturationPerSec: config.saturationPerSec,
    newSpanId,
  });

  configureDevLog(logger);

  return {
    logger,
    runId,
    ignoreUrls: [url],
    isSending: () => sending,
    flush: () => batcher.flush("manual"),
    uninstall: () => {
      batcher.close();
      configureDevLog(null);
    },
  };
}
